import { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useOpenAiGlobal } from "../use-openai-global";
import { MapPin, Maximize2, X } from "lucide-react";
import {
  FaAnchor,
  FaArrowsAltV,
  FaBaby,
  FaBed,
  FaBriefcase,
  FaBuilding,
  FaChild,
  FaChurch,
  FaClock,
  FaConciergeBell,
  FaDice,
  FaDumbbell,
  FaEye,
  FaGift,
  FaGlassCheers,
  FaGlassMartiniAlt,
  FaGolfBall,
  FaKey,
  FaLeaf,
  FaMountain,
  FaParking,
  FaPaw,
  FaShieldAlt,
  FaSmoking,
  FaSnowflake,
  FaSpa,
  FaSun,
  FaTag,
  FaTshirt,
  FaUmbrellaBeach,
  FaUsers,
  FaUtensils,
  FaWheelchair,
} from "react-icons/fa";
import { MdKitchen } from "react-icons/md";

/** Clean filled star for hotel ratings — no stroke, compact shape */
function FilledStar({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}
import { Image } from "@openai/apps-sdk-ui/components/Image";
import { Button } from "@openai/apps-sdk-ui/components/Button";
import useEmblaCarousel from "embla-carousel-react";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  return isMobile;
}

// Deterministic Italy coords per hotel index — returns [lat, lng] for Leaflet
function italyCoord(index: number): [number, number] {
  const seed = index * 137.5;
  const lat = 45.4642 + Math.sin(seed) * 0.015 + (index % 3) * 0.005;
  const lng = 9.19 + Math.cos(seed) * 0.02 + (index % 4) * 0.004;
  return [lat, lng];
}

const AMENITY_ICONS: Record<string, React.ComponentType<any>> = {
  "Discount - promo code": FaTag,
  "Day use / day pass": FaClock,
  "Special diet & allergy free breakfast": FaLeaf,
  "Special diet & allergy-free menu": FaLeaf,
  Parking: FaParking,
  "Marina & boat dock": FaAnchor,
  "Room service": FaConciergeBell,
  Kids: FaChild,
  Concierge: FaConciergeBell,
  "Connecting & close accommodation": FaBed,
  "Family accommodation": FaUsers,
  Smoking: FaSmoking,
  "Baby cot": FaBaby,
  "Iron and ironing board": FaTshirt,
  "Safe rooms": FaKey,
  "Fridge and minibar": MdKitchen,
  "Balcony & windows": FaSun,
  "room View": FaEye,
  Pets: FaPaw,
  Bar: FaGlassMartiniAlt,
  Spa: FaSpa,
  "Wellness & yoga": FaSpa,
  "Fitness gym": FaDumbbell,
  Rooftop: FaBuilding,
  Casino: FaDice,
  "Religion & faith": FaChurch,
  "Air conditioning / Heater": FaSnowflake,
  "Elevator and floor numbers": FaArrowsAltV,
  "Disabled guest": FaWheelchair,
  Shelter: FaShieldAlt,
  Restaurant: FaUtensils,
  Brunch: FaUtensils,
  Christmas: FaGift,
  "New Year's Eve": FaGlassCheers,
  Beaches: FaUmbrellaBeach,
  Golf: FaGolfBall,
  Ski: FaMountain,
  "Business meetings": FaBriefcase,
  "Events (wedding, birthday...)": FaGlassCheers,
};

type Hotel = {
  id: number | string;
  name: string;
  photo_url?: string;
  url?: string;
  country_code?: string;
  stars?: number;
  price?: number;
  amenities?: string[];
  latitude?: number;
  longitude?: number;
  location?: string;
  city?: string;
  country?: string;
  state?: string;
};

type HotelWithCoords = Hotel & { coords: [number, number] };

/* ── Hotel card used in both inline carousel and fullscreen sidebar ── */
function HotelCard({
  hotel,
  isSelected,
  onClick,
  compact,
}: {
  hotel: HotelWithCoords;
  isSelected: boolean;
  onClick: () => void;
  compact?: boolean;
}) {
  if (compact) {
    // Airbnb-style: photo background with name overlay
    return (
      <div
        className={
          "relative w-[140px] h-[100px] sm:w-[180px] sm:h-[130px] flex-none rounded-xl sm:rounded-2xl overflow-hidden cursor-pointer " +
          "shadow-lg ring ring-black/10 transition-all duration-200 hover:scale-[1.03] hover:shadow-xl " +
          (isSelected ? "ring-blue-500 ring-2" : "")
        }
        onClick={onClick}
      >
        {hotel.photo_url ? (
          <Image
            src={hotel.photo_url}
            alt={hotel.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
            <div className="text-center">
              <div className="text-white text-2xl mb-2">🏨</div>
              <span className="text-white text-xs font-medium px-2 truncate max-w-[120px] sm:max-w-[160px]">
                {hotel.name || "Hotel"}
              </span>
            </div>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-1.5 sm:p-2.5 text-white">
          <div className="text-xs sm:text-sm font-semibold truncate drop-shadow-sm">
            {hotel.name}
          </div>
          <div className="text-[10px] sm:text-[11px] text-white/80 flex items-center gap-1 sm:gap-1.5 mt-0.5">
            {hotel.stars ? (
              <span className="flex items-center gap-0.5">
                {Array.from({ length: hotel.stars }).map((_, i) => (
                  <FilledStar key={i} className="h-2.5 w-2.5 text-amber-400" />
                ))}
              </span>
            ) : null}
            {hotel.city || hotel.country_code ? (
              <span className="flex items-center gap-0.5 truncate">
                <MapPin className="h-2.5 w-2.5 flex-none" />{" "}
                {hotel.city || hotel.country_code}
              </span>
            ) : null}
          </div>
          {hotel.price ? (
            <div className="text-[10px] sm:text-[11px] font-semibold text-white mt-0.5 drop-shadow-sm">
              €{hotel.price}/night
            </div>
          ) : null}
          {hotel.amenities && hotel.amenities.length > 0 && (
            <div className="hidden sm:flex gap-1 mt-1">
              {hotel.amenities.slice(0, 3).map((a, i) => {
                const Icon = AMENITY_ICONS[a];
                return Icon ? (
                  <span key={i} className="text-white/70" title={a}>
                    <Icon className="h-2.5 w-2.5" />
                  </span>
                ) : null;
              })}
              {hotel.amenities.length > 3 && (
                <span className="text-[9px] text-white/60">
                  +{hotel.amenities.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Fullscreen sidebar card
  return (
    <div
      className={
        "rounded-2xl px-3 select-none cursor-pointer transition-colors duration-150 hover:bg-black/5" +
        (isSelected ? " bg-black/5" : "")
      }
    >
      <div
        className={`border-b ${isSelected ? "border-black/0" : "border-black/5"} hover:border-black/0`}
      >
        <div
          className="w-full text-left py-2 transition flex gap-3 items-center cursor-pointer"
          onClick={onClick}
        >
          {hotel.photo_url ? (
            <Image
              src={hotel.photo_url}
              alt={hotel.name}
              className="h-12 w-12 rounded-xl object-cover ring ring-black/5 flex-none"
            />
          ) : (
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center flex-none ring ring-black/5">
              <div className="text-white text-lg">🏨</div>
            </div>
          )}
          <div className="min-w-0 text-left flex-1">
            <div className="font-medium truncate text-sm">{hotel.name}</div>
            <div className="text-xs text-black/50 flex items-center gap-2 mt-0.5">
              {hotel.stars ? (
                <span className="flex items-center gap-0.5">
                  {Array.from({ length: hotel.stars }).map((_, i) => (
                    <FilledStar key={i} className="h-3 w-3 text-amber-400" />
                  ))}
                </span>
              ) : null}
              {hotel.city || hotel.country_code ? (
                <span className="flex items-center gap-0.5">
                  <MapPin className="h-3 w-3" />{" "}
                  {hotel.city || hotel.country_code}
                </span>
              ) : null}
              {hotel.price ? (
                <span className="text-black/70 font-medium">
                  Avg. price · €{hotel.price} / night
                </span>
              ) : null}
            </div>
            {hotel.amenities && hotel.amenities.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {hotel.amenities.slice(0, 5).map((a, i) => {
                  const Icon = AMENITY_ICONS[a];
                  return Icon ? (
                    <span key={i} className="text-black/40" title={a}>
                      <Icon className="h-3 w-3" />
                    </span>
                  ) : null;
                })}
                {hotel.amenities.length > 5 && (
                  <span className="text-[10px] text-black/40">
                    +{hotel.amenities.length - 5}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Inspector panel (fullscreen only, right side / bottom sheet on mobile) ── */
function Inspector({
  hotel,
  onClose,
  isMobile,
}: {
  hotel: HotelWithCoords;
  onClose: () => void;
  isMobile: boolean;
}) {
  return (
    <div
      className={
        isMobile
          ? "absolute z-30 inset-x-0 bottom-0 max-h-[60vh] pointer-events-auto"
          : "absolute z-30 top-2 bottom-4 right-2 w-[320px] pointer-events-auto"
      }
    >
      <div
        className={
          "relative overflow-y-auto bg-white text-black shadow-xl ring ring-black/10 " +
          (isMobile ? "rounded-t-2xl h-full" : "rounded-2xl h-full")
        }
      >
        <Button
          aria-label="Close details"
          className={
            "absolute z-10 top-3 shadow-lg rounded-full p-1.5 bg-white ring ring-black/10 hover:bg-white " +
            (isMobile ? "left-3" : "right-3")
          }
          variant="soft"
          color="secondary"
          size="sm"
          uniform
          onClick={onClose}
        >
          <X className="h-4 w-4 text-black" aria-hidden="true" />
        </Button>

        {hotel.photo_url ? (
          <Image
            src={hotel.photo_url}
            alt={hotel.name}
            className={
              "w-full object-cover " +
              (isMobile ? "h-32 rounded-t-2xl" : "h-48 rounded-t-2xl")
            }
          />
        ) : (
          <div
            className={
              "w-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center " +
              (isMobile ? "h-32 rounded-t-2xl" : "h-48 rounded-t-2xl")
            }
          >
            <div className="text-center">
              <div className="text-white text-4xl mb-2">🏨</div>
              <span className="text-white text-sm font-medium px-3">
                {hotel.name || "Hotel"}
              </span>
            </div>
          </div>
        )}

        <div className="p-4">
          <div className="text-xl font-medium">{hotel.name}</div>
          <div className="text-sm mt-1 text-black/60 flex items-center gap-2">
            {hotel.stars ? (
              <span className="flex items-center gap-0.5">
                {Array.from({ length: hotel.stars }).map((_, i) => (
                  <FilledStar key={i} className="h-3.5 w-3.5 text-amber-400" />
                ))}
              </span>
            ) : null}
            {hotel.city ? (
              <span className="flex items-center gap-0.5">
                <MapPin className="h-3.5 w-3.5" /> {hotel.city}
                {hotel.country ? `, ${hotel.country}` : ""}
              </span>
            ) : hotel.country_code ? (
              <span className="flex items-center gap-0.5">
                <MapPin className="h-3.5 w-3.5" /> {hotel.country_code}
              </span>
            ) : null}
          </div>
          {hotel.price ? (
            <div className="mt-2 text-lg font-semibold text-black">
              Avg. price · €{hotel.price} / night
            </div>
          ) : null}
          {hotel.location && (
            <div className="text-xs mt-1.5 text-black/40 leading-snug">
              {hotel.location}
            </div>
          )}

          <div className="mt-3 text-center">
            <Button
              color="primary"
              variant="solid"
              size="sm"
              className="rounded-lg w-full"
              onClick={() => {
                if ((window as any)?.openai?.sendFollowUpMessage) {
                  (window as any).openai.sendFollowUpMessage({
                    prompt: `Check available rooms at ${hotel.name} (hotel ID ${hotel.id})`,
                  });
                }
              }}
            >
              Find available rooms
            </Button>
          </div>

          {hotel.amenities && hotel.amenities.length > 0 && (
            <div className="mt-4">
              <div className="text-sm font-medium mb-2">Amenities</div>
              <div className="flex flex-wrap gap-1.5">
                {hotel.amenities.map((a, i) => {
                  const Icon = AMENITY_ICONS[a];
                  return (
                    <span
                      key={i}
                      className="text-xs bg-black/5 text-black/60 px-2 py-1 rounded-full inline-flex items-center gap-1"
                    >
                      {Icon && <Icon className="h-3 w-3 flex-none" />}
                      {a}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Main App ── */
function App() {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapObj = useRef<L.Map | null>(null);
  const markersRef = useRef<L.LayerGroup | null>(null);
  const [emblaRef] = useEmblaCarousel({ dragFree: true, loop: false });
  const isMobile = useIsMobile();

  const toolOutput = useOpenAiGlobal("toolOutput") as any;
  const displayMode = useOpenAiGlobal("displayMode");
  const isFullscreen = displayMode === "fullscreen";

  const [hotels, setHotels] = useState<HotelWithCoords[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [emptyState, setEmptyState] = useState<{
    found: boolean;
    reason: string;
    suggestedQuestions: string[];
  } | null>(null);

  // Parse hotels — coords are [lat, lng] for Leaflet
  useEffect(() => {
    if (!toolOutput) return;
    const raw: Hotel[] = toolOutput.hotels ?? [];
    if (raw.length > 0) {
      setEmptyState(null);
      setHotels(
        raw.map((h, i) => {
          const coords: [number, number] =
            h.latitude !== undefined && h.longitude !== undefined
              ? [h.latitude, h.longitude]
              : italyCoord(i);
          return { ...h, coords };
        }),
      );
    } else if (toolOutput.emptyState) {
      setHotels([]);
      setEmptyState(toolOutput.emptyState);
    }
  }, [toolOutput]);

  // Init Leaflet map
  useEffect(() => {
    if (mapObj.current || !mapRef.current) return;

    const cartoTiles =
      "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";

    const map = L.map(mapRef.current, {
      center: [48.8566, 2.3522],
      zoom: 5,
      zoomControl: true,
      attributionControl: false,
    });

    L.tileLayer(cartoTiles, {
      attribution: "&copy; OpenStreetMap &copy; CARTO",
      maxZoom: 20,
      subdomains: "abcd",
    }).addTo(map);

    markersRef.current = L.layerGroup().addTo(map);
    mapObj.current = map;

    setTimeout(() => map.invalidateSize(), 300);
    const resizeHandler = () => mapObj.current?.invalidateSize();
    window.addEventListener("resize", resizeHandler);
    return () => {
      window.removeEventListener("resize", resizeHandler);
      mapObj.current?.remove();
      mapObj.current = null;
    };
  }, []);

  // Markers + fit bounds
  useEffect(() => {
    if (!mapObj.current || !markersRef.current || hotels.length === 0) return;
    markersRef.current.clearLayers();

    const bounds: L.LatLngExpression[] = [];

    hotels.forEach((h) => {
      const marker = L.marker(h.coords, {
        icon: L.divIcon({
          html: '<span style="font-size:28px">🏨</span>',
          className: "",
          iconSize: [32, 32],
          iconAnchor: [16, 16],
        }),
      });
      marker.on("click", () => {
        setSelectedId(String(h.id));
        mapObj.current?.flyTo(h.coords, 15);
      });
      marker.addTo(markersRef.current!);
      bounds.push(h.coords);
    });

    if (bounds.length === 1) {
      mapObj.current.flyTo(bounds[0], 14);
    } else if (bounds.length > 1) {
      mapObj.current.fitBounds(L.latLngBounds(bounds), {
        padding: [40, 40],
        animate: true,
      });
    }
  }, [hotels]);

  // Invalidate size on mode change
  useEffect(() => {
    if (!mapObj.current) return;
    setTimeout(() => mapObj.current?.invalidateSize(), 300);
  }, [displayMode]);

  const selected = hotels.find((h) => String(h.id) === selectedId) || null;

  const selectHotel = (h: HotelWithCoords) => {
    setSelectedId(String(h.id));
    mapObj.current?.flyTo(h.coords, 15);
  };

  return (
    <div
      style={{
        height: isFullscreen ? "calc(100vh - 40px)" : isMobile ? 360 : 480,
      }}
      className={
        "relative antialiased w-full overflow-hidden " +
        (isMobile ? "min-h-[320px] " : "min-h-[480px] ") +
        (isFullscreen
          ? "rounded-none border-0"
          : "border border-black/10 dark:border-white/10 rounded-2xl")
      }
    >
      {/* ── Map (fills entire container) ── */}
      <div
        className={
          "absolute overflow-hidden z-0" +
          (isFullscreen
            ? isMobile
              ? " inset-0"
              : " left-[340px] right-2 top-2 bottom-4 border border-black/10 rounded-2xl"
            : " inset-0")
        }
        style={{ isolation: "isolate" }}
      >
        <div ref={mapRef} className="w-full h-full absolute inset-0" />
      </div>

      {/* ── Empty state info card ── */}
      {hotels.length === 0 && emptyState && (
        <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none">
          <div className="pointer-events-auto bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl ring ring-black/10 p-4 sm:p-6 max-w-sm mx-3 sm:mx-4 text-center text-black">
            <div className="text-2xl sm:text-3xl mb-2 sm:mb-3">🔍</div>
            <div className="text-sm sm:text-base font-semibold text-black">
              No matching hotels found
            </div>
            <div className="text-xs sm:text-sm text-black/50 mt-1 mb-3 sm:mb-4">
              Try a different search to find what you're looking for.
            </div>
            <div className="space-y-2">
              {emptyState.suggestedQuestions.map((q, i) => (
                <button
                  key={i}
                  className="w-full text-center text-xs sm:text-sm px-3 py-2.5 rounded-lg bg-black text-white font-semibold hover:bg-gray-800 transition-colors cursor-pointer"
                  onClick={() => {
                    if ((window as any)?.openai?.sendFollowUpMessage) {
                      (window as any).openai.sendFollowUpMessage({ prompt: q });
                    }
                  }}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Fullscreen button (inline only) ── */}
      {!isFullscreen && (
        <Button
          aria-label="Enter fullscreen"
          className="absolute top-2 right-2 sm:top-4 sm:right-4 z-30 shadow-lg pointer-events-auto bg-white text-black"
          color="secondary"
          size="sm"
          variant="soft"
          uniform
          onClick={() => {
            setSelectedId(null);
            if (window.openai?.requestDisplayMode) {
              window.openai.requestDisplayMode({
                mode: "fullscreen",
              });
            }
          }}
        >
          <Maximize2
            strokeWidth={1.5}
            className="h-4.5 w-4.5"
            aria-hidden="true"
          />
        </Button>
      )}

      {/* ── FULLSCREEN: Left sidebar list / bottom sheet on mobile ── */}
      {isFullscreen && (
        <div
          className={
            isMobile
              ? "absolute inset-x-0 bottom-0 z-20 h-[50vh] pointer-events-auto flex flex-col"
              : "absolute inset-y-0 bottom-4 left-0 z-20 w-[340px] max-w-[75%] pointer-events-auto"
          }
        >
          <div
            className={
              "flex flex-col bg-white text-black " +
              (isMobile
                ? "h-full rounded-t-2xl shadow-xl"
                : "h-full overflow-y-auto px-2")
            }
          >
            <div
              className={
                "flex justify-between flex-row items-center px-3 sm:px-5 flex-shrink-0 bg-white py-3 sm:py-4 text-sm sm:text-md font-medium" +
                (isMobile ? " border-b border-black/5" : "")
              }
            >
              {hotels.length} result{hotels.length !== 1 ? "s" : ""}
            </div>
            <div className={isMobile ? "overflow-y-auto flex-1 px-2" : ""}>
              {hotels.map((h) => (
                <HotelCard
                  key={String(h.id)}
                  hotel={h}
                  isSelected={selectedId === String(h.id)}
                  onClick={() => selectHotel(h)}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── FULLSCREEN: Right inspector / bottom sheet on mobile ── */}
      {isFullscreen && selected && (
        <Inspector
          hotel={selected}
          onClose={() => setSelectedId(null)}
          isMobile={isMobile}
        />
      )}

      {/* ── INLINE: Bottom horizontal carousel ── */}
      {!isFullscreen && (
        <div className="absolute inset-x-0 bottom-0 z-20 pointer-events-auto">
          <div className="pt-1 sm:pt-2 text-black">
            <div className="overflow-hidden" ref={emblaRef}>
              <div className="px-2 sm:px-3 py-2 sm:py-3 flex gap-2 sm:gap-3">
                {hotels.map((h) => (
                  <HotelCard
                    key={String(h.id)}
                    hotel={h}
                    isSelected={selectedId === String(h.id)}
                    onClick={() => selectHotel(h)}
                    compact
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── INLINE: Small popup on marker click ── */}
      {!isFullscreen && selected && (
        <div
          className={
            isMobile
              ? "absolute top-2 left-2 right-2 z-20 bg-white text-black rounded-xl shadow-lg ring ring-black/10 p-3 pointer-events-auto"
              : "absolute top-3 right-14 z-20 bg-white text-black rounded-xl shadow-lg ring ring-black/10 p-3 w-[210px] pointer-events-auto"
          }
        >
          <Button
            aria-label="Close details"
            className={
              "absolute top-2 shadow-lg rounded-full p-1.5 bg-white ring ring-black/10 hover:bg-white " +
              (isMobile ? "left-2" : "right-2")
            }
            variant="soft"
            color="secondary"
            size="sm"
            uniform
            onClick={() => setSelectedId(null)}
          >
            <X className="h-4 w-4 text-black" aria-hidden="true" />
          </Button>
          {selected.photo_url ? (
            <Image
              src={selected.photo_url}
              alt={selected.name}
              className="w-full h-24 rounded-xl object-cover mb-2"
            />
          ) : (
            <div className="w-full h-24 rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center mb-2">
              <div className="text-center">
                <div className="text-white text-2xl">🏨</div>
              </div>
            </div>
          )}
          <div className="text-sm font-medium truncate">{selected.name}</div>
          <div className="text-xs text-black/50 mt-0.5 flex items-center gap-1">
            {selected.stars ? (
              <span className="flex items-center gap-0.5">
                {Array.from({ length: selected.stars }).map((_, i) => (
                  <FilledStar key={i} className="h-2.5 w-2.5 text-amber-400" />
                ))}
              </span>
            ) : null}
            {selected.city || selected.country_code ? (
              <span>{selected.city || selected.country_code}</span>
            ) : null}
          </div>
          {selected.price ? (
            <div className="text-sm font-semibold text-black mt-1">
              Avg. price · €{selected.price} / night
            </div>
          ) : null}
          <Button
            color="primary"
            variant="solid"
            size="sm"
            className="w-full mt-2 rounded-lg"
            onClick={() => {
              if ((window as any)?.openai?.sendFollowUpMessage) {
                (window as any).openai.sendFollowUpMessage({
                  prompt: `Check available rooms at ${selected.name} (hotel ID ${selected.id})`,
                });
              }
            }}
          >
            Find available rooms
          </Button>
        </div>
      )}
    </div>
  );
}

createRoot(document.getElementById("hotel-map-root")!).render(<App />);
